import React, { useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
} from "@mui/material";
import { metricLabel } from "../../utils/metricOptions";
import { correlation } from "../../utils/stats";
import { useReportComparison } from "../../hooks/useReportData";
import type { CorrelationBlock } from "../../api/types";
import type { ComparisonGroup } from "../../../../../../shared/api/types";

interface CorrelationViewProps {
  block: CorrelationBlock;
  clientId: string | null;
  branding?: { primaryColor?: string; chartColor?: string };
  fill?: boolean;
}

interface Cell {
  r: number;
  n: number;
}

/** Diverging colour: red (r=-1) → neutral (r=0) → green (r=+1). */
const cellColor = (r: number): string => {
  const t = Math.max(-1, Math.min(1, r));
  if (t >= 0) {
    // white → green
    const g = Math.round(200 - 80 * t);
    return `rgb(${Math.round(230 - 150 * t)}, ${g + 20}, ${Math.round(
      230 - 150 * t,
    )})`;
  }
  // white → red
  const a = -t;
  return `rgb(${Math.round(230)}, ${Math.round(230 - 150 * a)}, ${Math.round(
    230 - 150 * a,
  )})`;
};

/**
 * Correlation matrix computed in-component: each comparison group is one data
 * point, and we compute the Pearson r between every pair of the block's metrics
 * across those groups. Cells are coloured red (negative) → green (positive).
 */
export const CorrelationView: React.FC<CorrelationViewProps> = ({
  block,
  clientId,
}) => {
  const theme = useTheme();
  const query = useReportComparison(
    block.dimension,
    block.metrics,
    block.filters,
    clientId,
  );

  const groups: ComparisonGroup[] = useMemo(
    () => query.data?.data?.groups ?? [],
    [query.data],
  );

  const matrix: Cell[][] = useMemo(() => {
    const metrics = block.metrics;
    return metrics.map((mi) =>
      metrics.map((mj) => {
        const pairs: [number, number][] = [];
        for (const g of groups) {
          const x = g.values?.[mi];
          const y = g.values?.[mj];
          if (x !== null && x !== undefined && y !== null && y !== undefined) {
            pairs.push([x, y]);
          }
        }
        return { r: correlation(pairs), n: pairs.length };
      }),
    );
  }, [block.metrics, groups]);

  if (query.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (query.isError) {
    return <Alert severity="error">Couldn't load correlation data.</Alert>;
  }

  if (groups.length < 2 || block.metrics.length < 2) {
    return (
      <Alert severity="info">
        Not enough segments to compute correlations for this period.
      </Alert>
    );
  }

  const labels = block.metrics.map((m) => metricLabel(m));
  const cellSize = 84;

  return (
    <Box sx={{ width: "100%" }}>
      {block.title && (
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          {block.title}
        </Typography>
      )}

      <Box sx={{ overflowX: "auto" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `140px repeat(${block.metrics.length}, ${cellSize}px)`,
            gap: "2px",
            alignItems: "stretch",
          }}
        >
          {/* Header row */}
          <Box />
          {labels.map((l, j) => (
            <Box
              key={`h-${j}`}
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                p: 0.5,
                fontSize: 11,
                fontWeight: 600,
                textAlign: "center",
                lineHeight: 1.1,
              }}
            >
              {l}
            </Box>
          ))}

          {/* Matrix rows */}
          {matrix.map((row, i) => (
            <React.Fragment key={`r-${i}`}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  pr: 1,
                  fontSize: 11,
                  fontWeight: 600,
                  justifyContent: "flex-end",
                  textAlign: "right",
                  lineHeight: 1.1,
                }}
              >
                {labels[i]}
              </Box>
              {row.map((cell, j) => (
                <Box
                  key={`c-${i}-${j}`}
                  sx={{
                    height: cellSize,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: i === j ? theme.palette.grey[300] : cellColor(cell.r),
                    color: theme.palette.getContrastText(
                      i === j ? theme.palette.grey[300] : cellColor(cell.r),
                    ),
                    borderRadius: 0.5,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                    {i === j ? "1.00" : cell.r.toFixed(2)}
                  </Typography>
                  <Typography sx={{ fontSize: 10, opacity: 0.8 }}>
                    n={cell.n}
                  </Typography>
                </Box>
              ))}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          −1
        </Typography>
        <Box
          sx={{
            width: 160,
            height: 12,
            borderRadius: 1,
            background: `linear-gradient(to right, ${cellColor(-1)}, ${cellColor(
              0,
            )}, ${cellColor(1)})`,
          }}
        />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          +1
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", ml: 1 }}>
          Pearson r across {groups.length} segments
        </Typography>
      </Box>
    </Box>
  );
};
